/**
 * Compléments pédagogiques des 10 pré-requis :
 * - risques : ce qui arrive si la compétence n'est pas maîtrisée
 * - influences : facteurs internes/externes qui dégradent la compétence
 * - qcm : mini QCM d'ancrage (3 questions) à faire avec l'élève en fin d'échange
 */

export const PREREQUIS_EXTRA = {
  mecanique: {
    risques: [
      "Ignorer un voyant rouge → casse moteur ou perte de freinage en pleine circulation.",
      "Freins surchauffés en longue descente → pédale molle, distance d'arrêt multipliée.",
      "Pneu sous-gonflé → éclatement possible à haute vitesse, perte de contrôle.",
      "Véhicule mal entretenu → contre-visite au contrôle technique, panne immobilisante.",
    ],
    influences: [
      "La pression du temps : « pas le temps de vérifier avant de partir ».",
      "L'excès de confiance dans une voiture récente (« elle est neuve, tout va bien »).",
      "Le coût : repousser l'entretien pour économiser (fausse économie).",
      "La méconnaissance : ne pas savoir lire le tableau de bord = ne rien anticiper.",
    ],
    qcm: [
      {
        q: "Un voyant ROUGE s'allume pendant que je roule. Que dois-je faire ?",
        options: ["Continuer jusqu'au prochain garage", "M'arrêter dès que possible en sécurité", "Accélérer pour rentrer plus vite"],
        correct: 1,
        explain: "Rouge = danger immédiat. On s'arrête en sécurité (bas-côté, parking) et on coupe le moteur.",
      },
      {
        q: "En longue descente, pour ralentir j'utilise en priorité…",
        options: ["La pédale de frein en continu", "Le frein moteur en rétrogradant", "Le frein à main"],
        correct: 1,
        explain: "Le frein moteur évite la surchauffe des plaquettes. La pédale sert en appoint seulement.",
      },
      {
        q: "Des pneus sous-gonflés…",
        options: ["N'ont aucun effet sur la conduite", "Allongent la distance de freinage", "Améliorent l'adhérence"],
        correct: 1,
        explain: "Sous-gonflage = distance de freinage jusqu'à x1.5 + risque d'éclatement + surconsommation.",
      },
    ],
  },

  synchronisation: {
    risques: [
      "Caler au milieu d'une intersection → exposé au trafic, panique, sur-accident.",
      "À-coups en manœuvre lente → heurter un piéton, un poteau, un autre véhicule.",
      "Regarder le levier de vitesses → 20 à 30 m parcourus en aveugle à 50 km/h.",
      "Pied gauche en appui constant sur l'embrayage → usure prématurée et perte de contrôle moteur.",
    ],
    influences: [
      "Le stress (examen, circulation dense) : la coordination fine se dégrade en premier.",
      "La fatigue : les gestes deviennent lents et imprécis.",
      "Changer de véhicule : le point de patinage n'est jamais exactement au même endroit.",
      "Les distractions (passagers, téléphone) : la synchronisation exige de l'attention tant qu'elle n'est pas automatisée.",
    ],
    qcm: [
      {
        q: "Le point de patinage, c'est…",
        options: ["Le moment où l'embrayage « prend » et où la voiture commence à avancer", "Quand les roues patinent sur sol mouillé", "La position pied à fond sur l'embrayage"],
        correct: 0,
        explain: "C'est LE repère du démarrage et des manœuvres lentes. Il se trouve toujours au même endroit sur une voiture donnée.",
      },
      {
        q: "En dessous de 30 km/h, pour ne pas caler je dois…",
        options: ["Freiner d'abord, débrayer après l'arrêt", "Débrayer avant/pendant le freinage", "Accélérer légèrement"],
        correct: 1,
        explain: "À basse vitesse, le moteur cale si on freine embrayé. Débrayer = garder le moteur en vie.",
      },
      {
        q: "Pour passer une vitesse, je regarde…",
        options: ["Le levier de vitesses", "La route, jamais le levier", "Mon pied gauche"],
        correct: 1,
        explain: "Le passage de rapport doit devenir un geste automatique. Les yeux restent sur la route.",
      },
    ],
  },

  regard: {
    risques: [
      "Découvrir l'obstacle trop tard → freinage d'urgence au lieu d'un simple lever de pied.",
      "Effet de fixation : on va là où on regarde… y compris dans l'obstacle.",
      "Ne pas détecter un piéton ou un cycliste qui s'engage.",
      "Hypnose des feux stop du véhicule devant → collision par l'arrière en chaîne.",
    ],
    influences: [
      "La fatigue : le champ visuel se rétrécit (vision « tunnel »).",
      "Le téléphone et les écrans : 1 seconde de regard baissé = 14 m parcourus à 50 km/h.",
      "La nuit et la pluie : moins d'informations visibles, il faut chercher l'information.",
      "Le stress du débutant : le regard se fige sur le capot au lieu de balayer loin.",
    ],
    qcm: [
      {
        q: "En ville, je place mon regard à environ…",
        options: ["5 m devant le capot", "50 à 80 m devant", "Uniquement dans les rétroviseurs"],
        correct: 1,
        explain: "Regarder loin = anticiper. Le près sert seulement à corriger, jamais à conduire.",
      },
      {
        q: "Face à un obstacle que je veux éviter, je regarde…",
        options: ["L'obstacle, pour le surveiller", "Là où je veux passer", "Mes rétroviseurs"],
        correct: 1,
        explain: "On va où on regarde. Fixer l'obstacle, c'est se diriger vers lui.",
      },
      {
        q: "Je contrôle mes rétroviseurs…",
        options: ["Toutes les 8 à 10 secondes", "Une fois par trajet", "Seulement avant de tourner"],
        correct: 0,
        explain: "Savoir en permanence ce qui se passe derrière = pouvoir décider vite devant.",
      },
    ],
  },

  trajectoire: {
    risques: [
      "Sortie de route en virage : 2ᵉ cause d'accident mortel sur route départementale.",
      "Mordre sur la voie inverse en coupant le virage → choc frontal.",
      "Freiner en pleine courbe → perte d'adhérence, tête-à-queue.",
      "Trajectoire imprévisible → les autres usagers ne peuvent plus anticiper.",
    ],
    influences: [
      "La vitesse excessive à l'entrée du virage : tout se joue AVANT la courbe.",
      "La chaussée dégradée : pluie, gravillons, feuilles mortes réduisent l'adhérence.",
      "La surconfiance sur un trajet connu (« je le prends tous les jours »).",
      "La fatigue : on « coupe » les virages sans s'en rendre compte.",
    ],
    qcm: [
      {
        q: "Dans un virage, je freine…",
        options: ["Dans le virage si besoin", "Avant le virage, en ligne droite", "Jamais, on accélère"],
        correct: 1,
        explain: "Freiner en courbe = perte d'adhérence. La vitesse s'adapte AVANT, on ré-accélère en sortie.",
      },
      {
        q: "La trajectoire sécurisée en virage, c'est…",
        options: ["Entrée large, corde, sortie large — sans quitter sa voie", "Couper au plus court", "Rester collé à l'axe central"],
        correct: 0,
        explain: "Large-corde-large « ouvre » le virage et maximise la visibilité, en restant dans SA voie.",
      },
      {
        q: "Si je freine fort en pleine courbe…",
        options: ["Rien de particulier", "Je risque une perte d'adhérence", "Le virage devient plus facile"],
        correct: 1,
        explain: "Les pneus ne peuvent pas tout faire : tourner ET freiner fort en même temps dépasse leur limite.",
      },
    ],
  },

  placement: {
    risques: [
      "Pincer un cycliste contre le trottoir → chute grave, responsabilité engagée.",
      "Être imprévisible pour les autres → refus de priorité, collision latérale.",
      "Mauvais pré-positionnement → changement de voie tardif et dangereux à l'intersection.",
      "Masquer la visibilité des autres usagers en roulant à cheval sur deux voies.",
    ],
    influences: [
      "Le trafic dense : la pression pousse à « se faufiler » n'importe où.",
      "Le GPS tardif : l'annonce au dernier moment provoque des placements brutaux.",
      "La méconnaissance de l'itinéraire : sans anticipation, pas de bon placement.",
      "La peur de gêner : rester « au milieu » pour ne choisir personne… gêne tout le monde.",
    ],
    qcm: [
      {
        q: "Distance latérale minimale pour dépasser un cycliste HORS agglomération :",
        options: ["0,5 m", "1 m", "1,5 m"],
        correct: 2,
        explain: "1,5 m hors agglo, 1 m en ville. Si c'est impossible, on reste derrière.",
      },
      {
        q: "Sur une route à 2 voies dans le même sens, la voie de gauche sert à…",
        options: ["Rouler plus vite", "Dépasser uniquement", "Éviter les insertions"],
        correct: 1,
        explain: "La voie de gauche est réservée au dépassement. On revient à droite dès que possible.",
      },
      {
        q: "Je choisis ma voie pour tourner à une intersection…",
        options: ["Au dernier moment", "Environ 100 m avant", "Après avoir franchi l'intersection"],
        correct: 1,
        explain: "Se pré-positionner tôt = être prévisible et lisible pour tous les autres usagers.",
      },
    ],
  },

  partage: {
    risques: [
      "45 % des tués sur la route sont des usagers vulnérables (ONISR).",
      "Dooring : ouvrir sa portière sans regarder peut tuer un cycliste.",
      "Rester dans l'angle mort d'un camion → écrasement lors de son changement de direction.",
      "Un coup de klaxon près d'un cycliste peut provoquer sa chute.",
    ],
    influences: [
      "L'impatience : « le cycliste me ralentit » → dépassement dangereux.",
      "Le sentiment de « propriété » de la route : la chaussée appartient à TOUS.",
      "La nuit et la pluie : les vulnérables deviennent quasi invisibles.",
      "L'habitude : en zone rurale on « n'attend » pas de cycliste, donc on ne le voit pas.",
    ],
    qcm: [
      {
        q: "Si je ne vois pas les rétroviseurs d'un camion…",
        options: ["Il me voit quand même", "Il ne me voit pas non plus", "Je peux le doubler par la droite"],
        correct: 1,
        explain: "Règle d'or : pas de contact visuel avec ses rétros = je suis dans son angle mort.",
      },
      {
        q: "Avant d'ouvrir ma portière côté chaussée…",
        options: ["Rien de particulier", "Contrôle rétro + regard arrière (main opposée sur la poignée)", "J'ouvre vite pour ne pas gêner"],
        correct: 1,
        explain: "La « poignée hollandaise » (main opposée) force la rotation du buste et le regard vers l'arrière.",
      },
      {
        q: "Un piéton s'engage sur un passage piéton…",
        options: ["Je klaxonne pour prévenir", "Je m'arrête : priorité absolue", "Je le contourne doucement"],
        correct: 1,
        explain: "Le piéton engagé OU manifestant l'intention de traverser a la priorité absolue (sanction : 6 points).",
      },
    ],
  },

  intersections: {
    risques: [
      "2 accidents mortels sur 3 en ville ont lieu à une intersection.",
      "La collision latérale est la plus violente : aucune zone de déformation côté portière.",
      "Un refus de priorité = faute éliminatoire à l'examen.",
      "Forcer le passage « dans le doute » = pari sur la vie des autres.",
    ],
    influences: [
      "La pression du véhicule qui suit : elle pousse à s'engager trop vite.",
      "L'habitude : « il n'y a jamais personne ici » → le jour où il y a quelqu'un…",
      "Le soleil rasant, la pluie, un véhicule stationné : visibilité amputée.",
      "Le stress : il fait forcer le passage au lieu de s'arrêter.",
    ],
    qcm: [
      {
        q: "Au STOP, je dois…",
        options: ["Ralentir fortement puis passer", "M'arrêter complètement, roues immobiles", "M'arrêter seulement s'il y a du trafic"],
        correct: 1,
        explain: "Arrêt TOTAL obligatoire, même à 4h du matin sans personne. Le « stop glissé » est éliminatoire.",
      },
      {
        q: "Dans un rond-point, je mets mon clignotant…",
        options: ["À l'entrée", "À la sortie", "Jamais"],
        correct: 1,
        explain: "Le clignotant droit s'active pour annoncer SA sortie (après la sortie précédant la sienne).",
      },
      {
        q: "En agglomération, sans panneau ni feu, la priorité est…",
        options: ["À gauche", "À droite", "Au plus rapide"],
        correct: 1,
        explain: "Priorité à droite par défaut : c'est LA règle de base en ville quand rien n'indique le contraire.",
      },
    ],
  },

  'controles-retros': {
    risques: [
      "Changement de voie sur une moto invisible dans l'angle mort → collision grave.",
      "Démarrer sur un cycliste placé dans l'angle mort droit.",
      "Dooring mortel en ouvrant la portière sans vérification.",
      "Reculer sur un piéton (enfant) invisible derrière la lunette.",
    ],
    influences: [
      "La routine : « je connais ma rue » → les contrôles sautent en premier.",
      "Des rétros mal réglés : on croit contrôler, mais on ne voit rien d'utile.",
      "La buée, la pluie, un chargement qui masque la lunette arrière.",
      "La précipitation : le contrôle épaule prend 1 seconde, la « gagner » peut coûter une vie.",
    ],
    qcm: [
      {
        q: "MMV signifie…",
        options: ["Miroir – Manœuvre – Vérification", "Moteur – Mains – Volant", "Marche – Montée – Virage"],
        correct: 0,
        explain: "Le réflexe avant TOUTE manœuvre : je contrôle (miroir), j'agis (manœuvre), je re-contrôle (vérification).",
      },
      {
        q: "Avant un changement de voie, je fais…",
        options: ["Rétro intérieur seulement", "Rétro intérieur + rétro côté + coup d'œil épaule", "Le clignotant suffit"],
        correct: 1,
        explain: "Le coup d'œil épaule est le SEUL moyen de voir dans l'angle mort. Le clignotant n'est pas un droit de passage.",
      },
      {
        q: "Des rétroviseurs bien réglés suppriment…",
        options: ["Environ 90 % de l'angle mort", "100 % de l'angle mort", "Rien du tout"],
        correct: 0,
        explain: "Bien réglés (20 % carrosserie / 80 % route), il reste toujours une petite zone → d'où le contrôle épaule.",
      },
    ],
  },

  allure: {
    risques: [
      "La distance d'arrêt augmente avec le CARRÉ de la vitesse : +10 km/h = beaucoup plus loin.",
      "Aquaplaning au-dessus de 80 km/h sur chaussée détrempée.",
      "Un choc à 80 km/h équivaut à une chute du 10ᵉ étage.",
      "Grand excès de vitesse : rétention de permis immédiate, jusqu'à 1 500 € et confiscation.",
    ],
    influences: [
      "Le retard et la pression horaire : « je rattrape le temps » (faux : 5 min gagnées sur 100 km).",
      "Le flux : « tout le monde roule à cette vitesse » n'est pas une excuse légale.",
      "La musique rythmée et l'excitation font monter l'allure sans qu'on s'en rende compte.",
      "La fatigue et l'alcool : ils dégradent l'estimation de sa propre vitesse.",
    ],
    qcm: [
      {
        q: "La règle des 2 secondes, c'est…",
        options: ["Le temps minimal entre moi et le véhicule qui me précède", "Le temps d'arrêt à un stop", "La durée minimale du clignotant"],
        correct: 0,
        explain: "Repère fixe (panneau, arbre) : le véhicule devant passe → je compte 2 s → je ne dois pas l'avoir atteint.",
      },
      {
        q: "Sous la pluie, la vitesse maximale sur autoroute est…",
        options: ["130 km/h", "110 km/h", "90 km/h"],
        correct: 1,
        explain: "Pluie = 110 sur autoroute, 100 sur voie rapide, 80 sur route. Et la distance de sécurité DOUBLE.",
      },
      {
        q: "Par brouillard avec visibilité inférieure à 50 m…",
        options: ["50 km/h maximum sur TOUTES les routes", "80 km/h maximum", "110 km/h maximum"],
        correct: 0,
        explain: "Visibilité < 50 m = 50 km/h max partout, même sur autoroute.",
      },
    ],
  },

  depassements: {
    risques: [
      "Le choc frontal en dépassement est le plus meurtrier de tous les accidents.",
      "Se rabattre trop tôt = queue de poisson, accrochage, sortie de route du dépassé.",
      "Suivre « aspiré » un véhicule qui double sans avoir sa propre visibilité.",
      "Frôler un cycliste en le dépassant → chute, blessures graves.",
    ],
    influences: [
      "L'impatience derrière un véhicule lent : 2 minutes « perdues » ne valent pas une vie.",
      "L'effet de groupe : « il est passé, je passe aussi » — sa fenêtre n'est pas la mienne.",
      "La surestimation de l'accélération de sa propre voiture.",
      "Les faux plats et cuvettes : une ligne droite « dégagée » peut cacher un véhicule.",
    ],
    qcm: [
      {
        q: "À 90 km/h, il me faut une visibilité d'au moins…",
        options: ["100 m", "400 m", "50 m"],
        correct: 1,
        explain: "Dépasser prend ~10 s à 90 km/h. Avec un véhicule en face à 90 aussi, il faut 400 m dégagés minimum.",
      },
      {
        q: "Je me rabats quand…",
        options: ["J'ai dépassé son pare-chocs avant", "Je vois le véhicule dépassé EN ENTIER dans mon rétro intérieur", "Le conducteur me fait un appel de phares"],
        correct: 1,
        explain: "Véhicule entier dans le rétro intérieur = distance suffisante pour se rabattre sans le gêner.",
      },
      {
        q: "Franchir une ligne continue pour dépasser un cycliste…",
        options: ["Toujours interdit", "Autorisé si la sécurité le permet (visibilité, écart suffisant)", "Obligatoire dans tous les cas"],
        correct: 1,
        explain: "Depuis 2015, chevaucher/franchir la ligne continue est autorisé pour dépasser un cycliste si c'est sans danger.",
      },
    ],
  },
};
